import { getOpenCV } from "./opencv";

import { Matrix, SingularValueDecomposition } from "ml-matrix";

import { debugLog } from "../../../utils/debug";

function matrixToArray(matrix) {
  if (Array.isArray(matrix)) {
    return matrix.map((row) => row.slice());
  }

  if (
    matrix &&
    typeof matrix.rows === "number" &&
    typeof matrix.columns === "number"
  ) {
    const result = [];

    for (let row = 0; row < matrix.rows; row += 1) {
      const values = [];

      for (let column = 0; column < matrix.columns; column += 1) {
        values.push(matrix.get(row, column));
      }

      result.push(values);
    }

    return result;
  }

  throw new Error("Unable to convert matrix to array.");
}

function multiply3x3(a, b) {
  const result = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];

  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < 3; column += 1) {
      for (let k = 0; k < 3; k += 1) {
        result[row][column] += a[row][k] * b[k][column];
      }
    }
  }

  return result;
}

function multiplyMatrixVector3(matrix, vector) {
  return [
    matrix[0][0] * vector[0] +
      matrix[0][1] * vector[1] +
      matrix[0][2] * vector[2],

    matrix[1][0] * vector[0] +
      matrix[1][1] * vector[1] +
      matrix[1][2] * vector[2],

    matrix[2][0] * vector[0] +
      matrix[2][1] * vector[1] +
      matrix[2][2] * vector[2],
  ];
}

function add3x3(a, b) {
  return a.map((row, rowIndex) =>
    row.map((value, columnIndex) => value + b[rowIndex][columnIndex]),
  );
}

function skewSymmetric(vector) {
  const [x, y, z] = vector;

  return [
    [0, -z, y],
    [z, 0, -x],
    [-y, x, 0],
  ];
}

function normalizeHomogeneous(vector) {
  const scale = vector[2];

  if (Math.abs(scale) > 1e-12) {
    return vector.map((value) => value / scale);
  }

  const norm = Math.hypot(vector[0], vector[1], vector[2]);

  if (norm < 1e-12) {
    throw new Error("Unable to normalize homogeneous vector.");
  }

  return vector.map((value) => value / norm);
}

function transformPoint(point, homography) {
  const transformed = multiplyMatrixVector3(homography, [point.x, point.y, 1]);

  if (Math.abs(transformed[2]) < 1e-12) {
    return {
      x: Number.NaN,
      y: Number.NaN,
    };
  }

  return {
    x: transformed[0] / transformed[2],
    y: transformed[1] / transformed[2],
  };
}

function findRightNullVector(matrix) {
  const decomposition = new SingularValueDecomposition(new Matrix(matrix), {
    autoTranspose: true,
  });

  const V = decomposition.rightSingularVectors;
  const column = V.columns - 1;

  return normalizeHomogeneous([
    V.get(0, column),
    V.get(1, column),
    V.get(2, column),
  ]);
}

function transpose3x3(matrix) {
  return [
    [matrix[0][0], matrix[1][0], matrix[2][0]],
    [matrix[0][1], matrix[1][1], matrix[2][1]],
    [matrix[0][2], matrix[1][2], matrix[2][2]],
  ];
}

function buildSecondRectifyingHomography(epipole, width, height) {
  const centerX = width / 2;
  const centerY = height / 2;

  const T = [
    [1, 0, -centerX],
    [0, 1, -centerY],
    [0, 0, 1],
  ];

  const TInverse = [
    [1, 0, centerX],
    [0, 1, centerY],
    [0, 0, 1],
  ];

  const translatedEpipole = multiplyMatrixVector3(T, epipole);

  const ex = translatedEpipole[0];
  const ey = translatedEpipole[1];
  const radius = Math.hypot(ex, ey);

  if (radius < 1e-12) {
    throw new Error(
      "Epipole lies too close to the image center for stable rectification.",
    );
  }

  // Rotate the epipole onto the X axis without changing its homogeneous sign.
  const alpha = ex / radius;
  const beta = ey / radius;

  const R = [
    [alpha, beta, 0],
    [-beta, alpha, 0],
    [0, 0, 1],
  ];

  const rotatedEpipole = multiplyMatrixVector3(R, translatedEpipole);
  const f = rotatedEpipole[0];

  if (!Number.isFinite(f) || Math.abs(f) < 1e-12) {
    throw new Error(
      "Unable to construct a stable epipolar projective transform.",
    );
  }

  debugLog(
    "[Reconstruction] Rectification epipole geometry:",
    JSON.stringify({
      translatedEpipole,
      rotatedEpipole,
      focalLikeDistance: f,
      imageWidth: width,
      imageHeight: height,
      normalizedDistanceFromCenter: Math.abs(f) / Math.max(width, height),
    }),
  );

  // G maps [f, 0, 1] to [f, 0, 0], sending the epipole to infinity.
  const G = [
    [1, 0, 0],
    [0, 1, 0],
    [-1 / f, 0, 1],
  ];

  return multiply3x3(TInverse, multiply3x3(G, multiply3x3(R, T)));
}

function solveAffineXAlignment(transformedA, transformedB) {
  const rows = [];
  const targets = [];

  for (let index = 0; index < transformedA.length; index += 1) {
    const pointA = transformedA[index];
    const pointB = transformedB[index];

    if (
      !Number.isFinite(pointA.x) ||
      !Number.isFinite(pointA.y) ||
      !Number.isFinite(pointB.x)
    ) {
      continue;
    }

    rows.push([pointA.x, pointA.y, 1]);
    targets.push([pointB.x]);
  }

  if (rows.length < 3) {
    throw new Error(
      "Not enough valid transformed correspondences for affine alignment.",
    );
  }

  const X = new Matrix(rows);
  const y = new Matrix(targets);

  const decomposition = new SingularValueDecomposition(X, {
    autoTranspose: true,
  });

  const solution = decomposition.solve(y);

  return [solution.get(0, 0), solution.get(1, 0), solution.get(2, 0)];
}

function summarizeVerticalErrors(errors) {
  const validErrors = errors
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (validErrors.length === 0) {
    return {
      validCorrespondences: 0,
      minimumVerticalError: null,
      medianVerticalError: null,
      meanVerticalError: null,
      maximumVerticalError: null,
    };
  }

  const total = validErrors.reduce((sum, value) => sum + value, 0);

  return {
    validCorrespondences: validErrors.length,
    minimumVerticalError: validErrors[0],
    medianVerticalError: validErrors[Math.floor(validErrors.length / 2)],
    meanVerticalError: total / validErrors.length,
    maximumVerticalError: validErrors[validErrors.length - 1],
  };
}

function formatDiagnosticValue(value) {
  return Number.isFinite(value) ? value.toFixed(3) : "invalid";
}

async function createImageFromMat(mat) {
  const cv = await getOpenCV();

  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");

    canvas.width = mat.cols;
    canvas.height = mat.rows;

    try {
      cv.imshow(canvas, mat);
    } catch (error) {
      reject(error);
      return;
    }

    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => {
      reject(new Error("Unable to create image from rectified OpenCV matrix."));
    };

    image.src = canvas.toDataURL("image/png");
  });
}

async function warpImage(source, homography, width, height) {
  const cv = await getOpenCV();

  const sourceMat = cv.imread(source);
  const destinationMat = new cv.Mat();
  const homographyMat = cv.matFromArray(3, 3, cv.CV_64F, homography.flat());

  try {
    cv.warpPerspective(
      sourceMat,
      destinationMat,
      homographyMat,
      new cv.Size(width, height),
      cv.INTER_LINEAR,
      cv.BORDER_CONSTANT,
      new cv.Scalar(0, 0, 0, 255),
    );

    return destinationMat;
  } finally {
    sourceMat.delete();
    homographyMat.delete();
  }
}

function computeBounds(points) {
  const validPoints = points.filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
  );

  if (validPoints.length === 0) {
    return null;
  }

  return {
    minX: Math.min(...validPoints.map((point) => point.x)),
    maxX: Math.max(...validPoints.map((point) => point.x)),
    minY: Math.min(...validPoints.map((point) => point.y)),
    maxY: Math.max(...validPoints.map((point) => point.y)),
  };
}

function buildCommonTranslation(transformedA, transformedB, width, height) {
  const combined = [...transformedA, ...transformedB];
  const bounds = computeBounds(combined);

  if (!bounds) {
    return [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
    ];
  }

  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return [
    [1, 0, width / 2 - centerX],
    [0, 1, height / 2 - centerY],
    [0, 0, 1],
  ];
}

export async function rectifyUncalibratedPair({
  imageElementA,
  imageElementB,
  fundamentalMatrix,
  correspondences,
}) {
  if (!imageElementA || !imageElementB) {
    throw new Error("Rectification requires both source images.");
  }

  if (!correspondences || correspondences.length < 8) {
    throw new Error("Rectification requires at least 8 correspondences.");
  }

  const widthA = imageElementA.naturalWidth || imageElementA.width;
  const heightA = imageElementA.naturalHeight || imageElementA.height;
  const widthB = imageElementB.naturalWidth || imageElementB.width;
  const heightB = imageElementB.naturalHeight || imageElementB.height;

  if (widthA !== widthB || heightA !== heightB) {
    throw new Error(
      "Current uncalibrated rectification requires equal image dimensions.",
    );
  }

  const F = matrixToArray(fundamentalMatrix);

  // For xB^T F xA = 0, the second-image epipole satisfies F^T eB = 0.
  const epipoleB = findRightNullVector(transpose3x3(F));

  const H2 = buildSecondRectifyingHomography(epipoleB, widthA, heightA);

  const pointsA = correspondences.map(
    (correspondence) => correspondence.pointA,
  );
  const pointsB = correspondences.map(
    (correspondence) => correspondence.pointB,
  );

  const transformedCornersB = [
    { x: 0, y: 0 },
    { x: widthA - 1, y: 0 },
    { x: 0, y: heightA - 1 },
    { x: widthA - 1, y: heightA - 1 },
  ].map((point) => transformPoint(point, H2));

  const transformedCenterB = transformPoint(
    {
      x: widthA / 2,
      y: heightA / 2,
    },
    H2,
  );

  debugLog(
    "[Reconstruction] H2 conditioning:",
    JSON.stringify({
      epipoleB,
      transformedCenterB,
      transformedCornersB,
      H2,
    }),
  );

  // Canonical projective camera associated with F: M = [eB]_x F + eB v^T.
  const skewEpipoleB = skewSymmetric(epipoleB);
  const base = multiply3x3(skewEpipoleB, F);
  const v = [1, 1, 1];

  const epipoleOuterV = [
    [epipoleB[0] * v[0], epipoleB[0] * v[1], epipoleB[0] * v[2]],
    [epipoleB[1] * v[0], epipoleB[1] * v[1], epipoleB[1] * v[2]],
    [epipoleB[2] * v[0], epipoleB[2] * v[1], epipoleB[2] * v[2]],
  ];

  const M = add3x3(base, epipoleOuterV);
  const H0 = multiply3x3(H2, M);

  // Validate vertical alignment before the horizontal affine correction.
  const debugA = pointsA.map((point) => transformPoint(point, H0));
  const debugB = pointsB.map((point) => transformPoint(point, H2));

  const debugVerticalErrors = debugA
    .map((pointA, index) => {
      const pointB = debugB[index];

      if (!Number.isFinite(pointA.y) || !Number.isFinite(pointB.y)) {
        return null;
      }

      return Math.abs(pointA.y - pointB.y);
    })
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  debugLog("[Reconstruction] Rectification pre-affine:", {
    medianVerticalError:
      debugVerticalErrors.length > 0
        ? debugVerticalErrors[Math.floor(debugVerticalErrors.length / 2)]
        : null,
    maximumVerticalError:
      debugVerticalErrors.length > 0
        ? debugVerticalErrors[debugVerticalErrors.length - 1]
        : null,
  });

  // Hartley's affine correction changes X while preserving rectified scanlines.
  const affineParameters = solveAffineXAlignment(debugA, debugB);

  const HA = [
    [affineParameters[0], affineParameters[1], affineParameters[2]],
    [0, 1, 0],
    [0, 0, 1],
  ];

  let H1 = multiply3x3(HA, H0);
  let finalH2 = H2;

  // Apply the same output translation to both rectified images.
  const preliminaryA = pointsA.map((point) => transformPoint(point, H1));
  const preliminaryB = pointsB.map((point) => transformPoint(point, finalH2));

  const commonTranslation = buildCommonTranslation(
    preliminaryA,
    preliminaryB,
    widthA,
    heightA,
  );

  H1 = multiply3x3(commonTranslation, H1);
  finalH2 = multiply3x3(commonTranslation, finalH2);

  const transformedA = pointsA.map((point) => transformPoint(point, H1));
  const transformedB = pointsB.map((point) => transformPoint(point, finalH2));

  const verticalErrorsBefore = pointsA.map((pointA, index) =>
    Math.abs(pointA.y - pointsB[index].y),
  );

  const verticalErrorsAfter = transformedA.map((pointA, index) =>
    Math.abs(pointA.y - transformedB[index].y),
  );

  const beforeDiagnostics = summarizeVerticalErrors(verticalErrorsBefore);
  const afterDiagnostics = summarizeVerticalErrors(verticalErrorsAfter);

  const beforeMedian = beforeDiagnostics.medianVerticalError;
  const afterMedian = afterDiagnostics.medianVerticalError;

  if (
    !Number.isFinite(afterMedian) ||
    afterMedian > 5 ||
    (Number.isFinite(beforeMedian) && afterMedian >= beforeMedian)
  ) {
    throw new Error(
      `Rectification validation failed: median vertical error ` +
        `${formatDiagnosticValue(beforeMedian)} px -> ` +
        `${formatDiagnosticValue(afterMedian)} px.`,
    );
  }

  // Rectified horizontal disparity is xA - xB.
  const disparities = transformedA
    .map((pointA, index) => {
      const pointB = transformedB[index];

      if (!Number.isFinite(pointA.x) || !Number.isFinite(pointB.x)) {
        return null;
      }

      return pointA.x - pointB.x;
    })
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  const medianDisparity =
    disparities.length > 0
      ? disparities[Math.floor(disparities.length / 2)]
      : null;

  const minimumDisparity = disparities.length > 0 ? disparities[0] : null;

  const maximumDisparity =
    disparities.length > 0 ? disparities[disparities.length - 1] : null;

  const [rectifiedMatA, rectifiedMatB] = await Promise.all([
    warpImage(imageElementA, H1, widthA, heightA),
    warpImage(imageElementB, finalH2, widthA, heightA),
  ]);

  try {
    const [rectifiedImageA, rectifiedImageB] = await Promise.all([
      createImageFromMat(rectifiedMatA),
      createImageFromMat(rectifiedMatB),
    ]);

    const diagnostics = {
      beforeMinimumVerticalError: beforeDiagnostics.minimumVerticalError,
      beforeMedianVerticalError: beforeDiagnostics.medianVerticalError,
      beforeMeanVerticalError: beforeDiagnostics.meanVerticalError,
      beforeMaximumVerticalError: beforeDiagnostics.maximumVerticalError,
      validCorrespondences: afterDiagnostics.validCorrespondences,
      minimumVerticalError: afterDiagnostics.minimumVerticalError,
      medianVerticalError: afterDiagnostics.medianVerticalError,
      meanVerticalError: afterDiagnostics.meanVerticalError,
      maximumVerticalError: afterDiagnostics.maximumVerticalError,
      minimumDisparity,
      medianDisparity,
      maximumDisparity,
    };

    debugLog(
      "[Reconstruction] Uncalibrated rectification:",
      JSON.stringify(diagnostics, null, 2),
    );

    return {
      imageA: rectifiedImageA,
      imageB: rectifiedImageB,
      homographyA: H1,
      homographyB: finalH2,
      transformedCorrespondences: transformedA.map((pointA, index) => ({
        pointA,
        pointB: transformedB[index],
      })),
      diagnostics,
      width: widthA,
      height: heightA,
    };
  } finally {
    rectifiedMatA.delete();
    rectifiedMatB.delete();
  }
}
